import Papa from "papaparse";
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchCSV } from "../utils/dataCache";
import { CDN } from "../config/cdn";
import { timeMindshare } from "../services/mindsharePerformance";
import { loadTikTokDataset } from "../services/tiktokData";

const TikTokDataContext = createContext();

export const useTikTokData = () => {
  const context = useContext(TikTokDataContext);
  if (!context) {
    throw new Error("useTikTokData must be used within TikTokDataProvider");
  }
  return context;
};

export const TikTokDataProvider = ({ children, enabled = true, dashboard = false }) => {
  const [metaData, setMetaData] = useState([]);
  const [tikTokSummary, setTikTokSummary] = useState([]);
  const [tikTokManifest, setTikTokManifest] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [allTags, setAllTags] = useState([]);
  const [allPublishers, setAllPublishers] = useState([]);
  const [allGenres, setAllGenres] = useState([]);
  const [allDevelopers, setAllDevelopers] = useState([]);
  const [allLiveService, setAllLiveService] = useState([]);
  const [allPricing, setAllPricing] = useState([]);
  const [allUGC, setAllUGC] = useState([]);
  const [allFocus, setAllFocus] = useState([]);
  const [allPlatforms, setAllPlatforms] = useState([]);
  const [releaseDateRange, setReleaseDateRange] = useState([null, null]);

  const [refreshNonce, setRefreshNonce] = useState(0);

  const refreshTikTokData = useCallback(() => {
    setRefreshNonce(Date.now());
  }, []);

  useEffect(() => {
  if (!enabled) {
    setLoading(false);
    return;
  }
  let cancelled = false;

  const loadTikTokData = async () => {
    try {
      setLoading(true);
      setError(null);

      const metaBase =
        CDN.mindshare || "https://mindgamedataapi.s3.us-east-1.amazonaws.com";
      const [metaCsv, dataset] = await Promise.all([
        fetchCSV(`${metaBase}/metadata.csv.gz`, { ttl: 10 * 60 * 1000 }),
        dashboard ? loadTikTokDataset({ force: true }) :
          fetchCSV(`${CDN.tiktok}/tiktok_summary.csv.gz`, { ttl: 60000 }).then(csv => ({
            rows: Papa.parse(csv.trim(), { header: true, skipEmptyLines: true }).data,
            manifest: null,
          })),
      ]);

      const parsedMetaData = timeMindshare("provider-metadata-parse", () => Papa.parse(metaCsv.trim(), {
        header: true,
        skipEmptyLines: true,
      }).data);

      if (cancelled) return;

      setMetaData(parsedMetaData);
      setTikTokSummary(dataset.rows);
      setTikTokManifest(dataset.manifest);
    } catch (err) {
      if (cancelled) return;
      console.error("❌ Failed to load TikTok data:", err);
      setError(err?.message || "Failed to load TikTok data");
    } finally {
      if (!cancelled) setLoading(false);
    }
  };

  loadTikTokData();

  const interval = setInterval(loadTikTokData, 60 * 1000);

  return () => {
    cancelled = true;
    clearInterval(interval);
  };
}, [refreshNonce, enabled, dashboard]);

  const getAllTags = () => {
    const tagSet = new Set();
    metaData.forEach((game) => {
      if (game.Tags) {
        game.Tags.split(",").forEach((tag) => {
          const cleaned = tag.trim();
          if (cleaned) tagSet.add(cleaned);
        });
      }
    });
    const result = [...tagSet].sort();
    setAllTags(result);
    return result;
  };

  const getAllPublishers = () => {
    const publisherSet = new Set();
    metaData.forEach((game) => {
      const val = game.Publisher?.trim();
      if (val) publisherSet.add(val);
    });
    const result = [...publisherSet].sort();
    setAllPublishers(result);
    return result;
  };

  const getAllGenres = () => {
    const genreSet = new Set();
    metaData.forEach((game) => {
      if (game.Genre) {
        game.Genre.split(",").forEach((genre) => {
          const trimmed = genre.trim();
          if (trimmed) genreSet.add(trimmed);
        });
      }
    });
    const result = [...genreSet].sort();
    setAllGenres(result);
    return result;
  };

  const getAllDevelopers = () => {
    const developersSet = new Set();
    metaData.forEach((game) => {
      const val = game.Developer?.trim();
      if (val) developersSet.add(val);
    });
    const result = [...developersSet].sort();
    setAllDevelopers(result);
    return result;
  };

  const getAllLiveServiceOptions = () => {
    const set = new Set();
    metaData.forEach((game) => {
      const val = game.Live_Service_Static?.trim();
      if (val) set.add(val);
    });
    const result = [...set].sort();
    setAllLiveService(result);
    return result;
  };

  const getAllPricingOptions = () => {
    const set = new Set();
    metaData.forEach((game) => {
      const val = game.Pricing_Monetization?.trim();
      if (val) set.add(val);
    });
    const result = [...set].sort();
    setAllPricing(result);
    return result;
  };

  const getAllUGCOptions = () => {
    const set = new Set();
    metaData.forEach((game) => {
      const val = game["User_Generated_Content_(UGC)"]?.trim();
      if (val) set.add(val);
    });
    const result = [...set].sort();
    setAllUGC(result);
    return result;
  };

  const getAllFocusOptions = () => {
    const set = new Set();
    metaData.forEach((game) => {
      if (game.Single_Multiplayer_Focus) {
        let value = game.Single_Multiplayer_Focus.trim();
        const normalized = value.toLowerCase();
        if (normalized === "single-player with multiplayer") {
          value = "Single-Player with Multiplayer";
        }
        if (value) set.add(value);
      }
    });
    const result = [...set].sort();
    setAllFocus(result);
    return result;
  };

  const getAllPlatformGroups = () => {
    const set = new Set();
    metaData.forEach((game) => {
      if (game.Platform_Group_Filter) {
        game.Platform_Group_Filter.split(",").forEach((p) => {
          const cleaned = p.trim();
          if (cleaned) set.add(cleaned);
        });
      }
    });
    const result = [...set].sort();
    setAllPlatforms(result);
    return result;
  };

  const getAllReleaseDates = () => {
    const dates = metaData
      .map((game) => {
        const dateStr = game.Release_Date?.trim();
        if (!dateStr) return null;
        const date = new Date(dateStr);
        return isNaN(date) ? null : date;
      })
      .filter(Boolean)
      .sort((a, b) => a - b);

    if (dates.length === 0) return [null, null];

    const minDate = dates[0];
    const maxDate = dates[dates.length - 1];
    const result = [minDate, maxDate];
    setReleaseDateRange(result);
    return result;
  };

  const value = {
    metaData,
    tikTokSummary,
    tikTokManifest,
    allTags,
    allPublishers,
    allGenres,
    allDevelopers,
    allLiveService,
    allPricing,
    allUGC,
    allFocus,
    allPlatforms,
    releaseDateRange,
    loading,
    error,
    refreshTikTokData,
    getAllTags,
    getAllPublishers,
    getAllGenres,
    getAllDevelopers,
    getAllLiveServiceOptions,
    getAllPricingOptions,
    getAllUGCOptions,
    getAllFocusOptions,
    getAllPlatformGroups,
    getAllReleaseDates,
  };

  return (
    <TikTokDataContext.Provider value={value}>
      {children}
    </TikTokDataContext.Provider>
  );
};